/**
 * Keeps track of the media uploads in flight, and how to cancel them.
 *
 * The handles live on the module rather than in the upload panel, so they
 * survive that panel being closed mid-upload — the floating progress card can
 * still cancel an upload started from a tool the user has since left.
 *
 * More than one upload runs at a time whenever the files are of different
 * media kinds: the media store's busy flag is per bucket, so pasting an image
 * while a video uploads starts a second upload rather than queueing it. Each
 * gets its own entry here, and cancelling one leaves the others alone.
 *
 * Mirrors `services/export/exportSession.ts`, which does the same for exports.
 */

import axios from 'axios';
import { useUploadProgressStore } from '@/stores/uploadProgress';
import { createUid } from '@/utils/uid';
import type { CancelTokenSource } from 'axios';
import type { UploadHandle } from '@/types/shared/uploadProgress';

const handles = new Map<string, UploadHandle>();

/**
 * How many uploads the user has cancelled this session. A batch loop cannot
 * see the id of the upload it just awaited — that is created inside
 * `uploadMedia` — so it snapshots this before each file and compares after,
 * which tells it a cancel happened without needing to know which one.
 */
let cancelCount = 0;

/** Start an upload and get its id and cancel handles. */
export function beginUpload(
  fileName: string,
  bytesTotal: number,
  chunkCount: number
): { id: string; signal: AbortSignal; cancelToken: CancelTokenSource['token'] } {
  const id = createUid();
  const handle: UploadHandle = {
    controller: new AbortController(),
    source: axios.CancelToken.source(),
    cancelled: false
  };
  handles.set(id, handle);
  useUploadProgressStore().begin(id, fileName, bytesTotal, chunkCount);

  return {
    id,
    signal: handle.controller.signal,
    cancelToken: handle.source.token
  };
}

/** Report total bytes sent so far, and which chunk they came from. */
export function reportUploadProgress(
  id: string,
  bytesSent: number,
  chunkIndex: number
): void {
  useUploadProgressStore().report(id, bytesSent, chunkIndex);
}

/**
 * Cancel one upload. The chunks already sent are abandoned rather than
 * resumed: the caller drops its cache key, so a retry starts over.
 */
export function cancelUpload(id: string): void {
  const handle = handles.get(id);
  if (!handle || handle.cancelled) return;

  handle.cancelled = true;
  cancelCount += 1;
  useUploadProgressStore().markCancelling(id);
  handle.controller.abort();
  handle.source.cancel();
}

/**
 * Whether the user cancelled this upload. Callers use it to stay quiet — a
 * cancel is not a failure worth a snackbar — and to abandon the rest of a
 * batch rather than moving straight on to the next file.
 *
 * The handle outlives the aborted request on purpose: a batch cancelled on
 * its second file asks this after that file has unwound, to decide whether to
 * start the third.
 */
export function uploadCancelledByUser(id: string): boolean {
  return handles.get(id)?.cancelled === true;
}

/**
 * A running total of user cancellations. Compare a value taken before an
 * upload with one taken after to learn whether it was cancelled.
 */
export function userCancelCount(): number {
  return cancelCount;
}

/** Clear the handle once an upload has finished, failed or been cancelled. */
export function endUpload(id: string): void {
  useUploadProgressStore().end(id);
  handles.delete(id);
}
